export type RecoveryStorage = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
  keys: () => Promise<string[]>;
};

let _recoveryStorage: RecoveryStorage | undefined;

export const configureLiveRecordingRecoveryStorage = (
  storage: RecoveryStorage,
): void => {
  _recoveryStorage = storage;
};

const storage = (): RecoveryStorage => {
  if (!_recoveryStorage) {
    throw new Error("Live recording recovery storage has not been configured");
  }
  return _recoveryStorage;
};

export type LiveRecordingRecoveryPlatform = "android" | "ios" | "web";
export type LiveRecordingRecoveryStatus = "recording" | "uploading" | "blocked";

export type LiveRecordingRecoveryFormat = {
  sampleRate: number;
  originalSampleRate?: number;
  channels: number;
  bitsPerSample: number;
};

export interface LiveRecordingRecoveryManifest {
  transcriptId: string;
  platform: LiveRecordingRecoveryPlatform;
  status: LiveRecordingRecoveryStatus;
  startedAt: number;
  format?: LiveRecordingRecoveryFormat;
  uploadedBytes?: number;
  lastUploadedSequenceNumber?: number;
  finalSequenceNumber?: number;
  lastErrorMessage?: string | null;
  updatedAt: number;
}

const LIVE_RECORDING_RECOVERY_KEY_PREFIX = "live_recording_recovery_v2:";
const LIVE_RECORDING_RECOVERY_V1_KEY = "live_recording_recovery_v1";

const buildRecoveryKey = (transcriptId: string) =>
  `${LIVE_RECORDING_RECOVERY_KEY_PREFIX}${transcriptId}`;

const parseManifest = (
  value: string | null,
): LiveRecordingRecoveryManifest | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Partial<LiveRecordingRecoveryManifest>;
    if (
      !parsed ||
      typeof parsed.transcriptId !== "string" ||
      !["android", "ios", "web"].includes(parsed.platform ?? "") ||
      !["recording", "uploading", "blocked"].includes(parsed.status ?? "") ||
      typeof parsed.startedAt !== "number" ||
      (parsed.format !== undefined &&
        (typeof parsed.format.sampleRate !== "number" ||
          (parsed.format.originalSampleRate !== undefined &&
            typeof parsed.format.originalSampleRate !== "number") ||
          typeof parsed.format.channels !== "number" ||
          typeof parsed.format.bitsPerSample !== "number")) ||
      (parsed.uploadedBytes !== undefined &&
        typeof parsed.uploadedBytes !== "number") ||
      (parsed.lastErrorMessage !== undefined &&
        parsed.lastErrorMessage !== null &&
        typeof parsed.lastErrorMessage !== "string") ||
      (parsed.lastUploadedSequenceNumber !== undefined && (!Number.isSafeInteger(parsed.lastUploadedSequenceNumber) || parsed.lastUploadedSequenceNumber < 0)) ||
      (parsed.finalSequenceNumber !== undefined && (!Number.isSafeInteger(parsed.finalSequenceNumber) || parsed.finalSequenceNumber < 0)) ||
      typeof parsed.updatedAt !== "number"
    ) {
      return null;
    }

    return {
      transcriptId: parsed.transcriptId,
      platform: parsed.platform as LiveRecordingRecoveryPlatform,
      status: parsed.status as LiveRecordingRecoveryStatus,
      startedAt: parsed.startedAt,
      format: parsed.format,
      uploadedBytes: parsed.uploadedBytes,
      lastUploadedSequenceNumber: parsed.lastUploadedSequenceNumber,
      finalSequenceNumber: parsed.finalSequenceNumber,
      lastErrorMessage: parsed.lastErrorMessage ?? null,
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
};

// One-time migration from the v1 single-key format to the v2 per-transcript
// format. Idempotent: removes the v1 key once migrated.
const migrateV1ManifestIfPresent = async () => {
  try {
    const value = await storage().getItem(LIVE_RECORDING_RECOVERY_V1_KEY);
    if (!value) {
      return;
    }

    const v1Manifest = parseManifest(value);
    if (v1Manifest) {
      await storage().setItem(
        buildRecoveryKey(v1Manifest.transcriptId),
        JSON.stringify(v1Manifest),
      );
    }
    await storage().removeItem(LIVE_RECORDING_RECOVERY_V1_KEY);
  } catch (error) {
    console.error("Failed to migrate the v1 recovery manifest:", error);
  }
};

export const listLiveRecordingRecoveryManifests = async (): Promise<LiveRecordingRecoveryManifest[]> => {
  await migrateV1ManifestIfPresent();

  try {
    const keys = await storage().keys();
    const manifestKeys = keys.filter((key) =>
      key.startsWith(LIVE_RECORDING_RECOVERY_KEY_PREFIX),
    );
    const manifests = await Promise.all(
      manifestKeys.map(async (key) => {
        const value = await storage().getItem(key);
        return parseManifest(value);
      }),
    );
    return manifests.filter(
      (manifest): manifest is LiveRecordingRecoveryManifest =>
        manifest !== null,
    );
  } catch (error) {
    console.error(
      "Failed to enumerate live recording recovery manifests:",
      error,
    );
    return [];
  }
};

// Returns the most recently updated manifest (any platform). Used by callers
// that just want to know "is there anything to recover?".
export const getLiveRecordingRecoveryManifest = async () => {
  const manifests = await listLiveRecordingRecoveryManifests();
  if (manifests.length === 0) {
    return null;
  }
  return manifests.reduce((latest, current) =>
    current.updatedAt > latest.updatedAt ? current : latest,
  );
};

export const getLiveRecordingRecoveryForPlatform = async (
  platform: LiveRecordingRecoveryPlatform,
  excludeTranscriptIds: readonly string[] = [],
) => {
  const manifests = await listLiveRecordingRecoveryManifests();
  const platformManifests = manifests.filter((m) => m.platform === platform && !excludeTranscriptIds.includes(m.transcriptId));
  if (platformManifests.length === 0) {
    return null;
  }
  return platformManifests.reduce((latest, current) =>
    current.updatedAt > latest.updatedAt ? current : latest,
  );
};

// Returns true if the manifest was persisted, false if Preferences storage failed.
// Callers can react (e.g. show a "backup unavailable" warning) if they care.
export const setLiveRecordingRecoveryManifest = async (
  params: Omit<LiveRecordingRecoveryManifest, "updatedAt"> & {
    updatedAt?: number;
  },
): Promise<boolean> => {
  const manifest: LiveRecordingRecoveryManifest = {
    ...params,
    updatedAt: params.updatedAt ?? Date.now(),
  };

  try {
    await storage().setItem(
      buildRecoveryKey(manifest.transcriptId),
      JSON.stringify(manifest),
    );
    return true;
  } catch (error) {
    console.error("Failed to save live recording recovery manifest:", error);
    return false;
  }
};

// Looks up a specific manifest by transcriptId (direct key access, no scan).
export const getLiveRecordingRecoveryForTranscript = async (
  transcriptId: string,
): Promise<LiveRecordingRecoveryManifest | null> => {
  await migrateV1ManifestIfPresent();
  try {
    const value = await storage().getItem(buildRecoveryKey(transcriptId));
    return parseManifest(value);
  } catch (error) {
    console.error("Failed to get recovery manifest for transcript:", error);
    return null;
  }
};

// Update exactly one recovery manifest. Transcript identity is mandatory so
// concurrent tabs/accounts can never update whichever platform entry happens
// to be newest.
const manifestUpdates = new Map<string, Promise<boolean>>();
export const touchLiveRecordingRecoveryManifest = (
  transcriptId: string,
  updates?: Partial<Pick<LiveRecordingRecoveryManifest, "status" | "format" | "uploadedBytes" | "lastErrorMessage" | "lastUploadedSequenceNumber" | "finalSequenceNumber">>,
): Promise<boolean> => {
  const pending = (manifestUpdates.get(transcriptId) ?? Promise.resolve(true))
    .then(() => updateManifest(transcriptId, updates));
  manifestUpdates.set(transcriptId, pending);
  void pending.finally(() => { if (manifestUpdates.get(transcriptId) === pending) manifestUpdates.delete(transcriptId); });
  return pending;
};

const updateManifest = async (
  transcriptId: string,
  updates?: Partial<
    Pick<
      LiveRecordingRecoveryManifest,
      "status" | "format" | "uploadedBytes" | "lastErrorMessage" | "lastUploadedSequenceNumber" | "finalSequenceNumber"
    >
  >,
): Promise<boolean> => {
  const manifest = await getLiveRecordingRecoveryForTranscript(transcriptId);
  if (!manifest) {
    return false;
  }

  const hasLastErrorMessageUpdate =
    updates !== undefined && "lastErrorMessage" in updates;

  return setLiveRecordingRecoveryManifest({
    ...manifest,
    status: updates?.status ?? manifest.status,
    format: updates?.format ?? manifest.format,
    uploadedBytes: Math.max(updates?.uploadedBytes ?? 0, manifest.uploadedBytes ?? 0),
    lastUploadedSequenceNumber: Math.max(updates?.lastUploadedSequenceNumber ?? 0, manifest.lastUploadedSequenceNumber ?? 0),
    finalSequenceNumber: updates?.finalSequenceNumber ?? manifest.finalSequenceNumber,
    lastErrorMessage: hasLastErrorMessageUpdate
      ? (updates?.lastErrorMessage ?? null)
      : manifest.lastErrorMessage,
    updatedAt: Date.now(),
  });
};

export const getWebRecoveryFormat = (
  manifest: LiveRecordingRecoveryManifest,
): LiveRecordingRecoveryFormat =>
  manifest.format || {
    sampleRate: 16000,
    channels: 1,
    bitsPerSample: 16,
  };

export const clearLiveRecordingRecoveryManifest = async (
  transcriptId: string | undefined,
) => {
  // Refuse to clear all manifests by accident. Several call sites pass
  // liveRecording?.itemId which can be undefined; in v1 of this storage that
  // wiped the single global slot, but in v2 (per-transcript keys) it would
  // wipe every recoverable session at once. Require an explicit id - if the
  // caller really wants to wipe everything they should call clearAll below.
  if (!transcriptId) {
    return;
  }

  try {
    await storage().removeItem(buildRecoveryKey(transcriptId));
  } catch (error) {
    console.error("Failed to clear live recording recovery manifest:", error);
  }
};

// Explicit "clear everything" path for callers that genuinely want to wipe
// every recoverable session (e.g. on logout). Not exported through the
// recovery manifest API by accident - use this only when you're sure.
export const clearAllLiveRecordingRecoveryManifests = async () => {
  try {
    const manifests = await listLiveRecordingRecoveryManifests();
    await Promise.all(
      manifests.map((manifest) =>
        storage().removeItem(buildRecoveryKey(manifest.transcriptId)),
      ),
    );
  } catch (error) {
    console.error(
      "Failed to clear all live recording recovery manifests:",
      error,
    );
  }
};
